import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllPages } from "../api/client";
import * as tm from "../api/tm";

const K = {
  tasks: ["tm", "tasks"] as const,
  task: (id: string) => ["tm", "task", id] as const,
  feed: ["tm", "me"] as const,
  lists: ["tm", "lists"] as const,
  dashboard: ["tm", "dashboard"] as const,
  sub: (id: string, res: string) => ["tm", "task", id, res] as const,
};

/** Toutes les tâches du tenant visibles par l'appelant (drainées). */
export function useTasks() {
  return useQuery({
    queryKey: K.tasks,
    queryFn: () =>
      fetchAllPages((cursor) => tm.listTasks(cursor ? { cursor } : undefined)),
    select: (d) => d.items,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: K.task(id ?? ""),
    queryFn: () => tm.getTask(id!),
    enabled: !!id,
  });
}

/** Feed unifié /tasks/me — tâches natives + miroirs répliqués PM (DT F-04). */
export function useMyFeed() {
  return useQuery({
    queryKey: K.feed,
    queryFn: () => fetchAllPages((cursor) => tm.getMyFeed(cursor)),
    select: (d) => d.items,
  });
}

export function useDashboard() {
  return useQuery({ queryKey: K.dashboard, queryFn: tm.getDashboard });
}

export function useLists() {
  return useQuery({
    queryKey: K.lists,
    queryFn: () => tm.listLists(),
    select: (d) => d.items,
  });
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K.tasks });
    void qc.invalidateQueries({ queryKey: K.feed });
    void qc.invalidateQueries({ queryKey: K.dashboard });
    // Préfixe ["tm","task"] → couvre le détail ET toutes les sous-ressources
    // (subtasks, assignments, comments…) d'une tâche en une invalidation.
    void qc.invalidateQueries({ queryKey: ["tm", "task"] });
  };
}

export function useCreateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (body: tm.CreateTaskInput) => tm.createTask(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: tm.UpdateTaskInput }) =>
      tm.updateTask(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useCompleteTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (id: string) => tm.completeTask(id),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (id: string) => tm.deleteTask(id),
    onSuccess: () => invalidate(),
  });
}

// ── Ressources d'une tâche (panneau détail) ─────────────────────────────────

export function useSubtasks(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "subtasks"),
    queryFn: () => tm.listSubtasks(taskId!),
    enabled: !!taskId,
    select: (d) => d.items,
  });
}

export function useAssignments(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "assignments"),
    queryFn: () => tm.listAssignments(taskId!),
    enabled: !!taskId,
    select: (d) => d.items.filter((a) => a.active),
  });
}

export function useComments(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "comments"),
    queryFn: () => tm.listComments(taskId!),
    enabled: !!taskId,
    select: (d) => d.items,
  });
}

export function useAttachments(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "attachments"),
    queryFn: () => tm.listAttachments(taskId!),
    enabled: !!taskId,
    select: (d) => d.items,
  });
}

export function useBlockers(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "blockers"),
    queryFn: () => tm.listBlockers(taskId!),
    enabled: !!taskId,
    select: (d) => d.items,
  });
}

export function useAcl(taskId: string | null) {
  return useQuery({
    queryKey: K.sub(taskId ?? "", "acl"),
    queryFn: () => tm.listAcl(taskId!),
    enabled: !!taskId,
    select: (d) => d.items,
  });
}

function useInvalidateSub(res: string) {
  const qc = useQueryClient();
  return (taskId: string) =>
    void qc.invalidateQueries({ queryKey: K.sub(taskId, res) });
}

export function useAddAssignment() {
  const invalidate = useInvalidateSub("assignments");
  return useMutation({
    mutationFn: (v: {
      taskId: string;
      body: Parameters<typeof tm.addAssignment>[1];
    }) => tm.addAssignment(v.taskId, v.body),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useRemoveAssignment() {
  const invalidate = useInvalidateSub("assignments");
  return useMutation({
    mutationFn: (v: { taskId: string; assignmentId: string }) =>
      tm.removeAssignment(v.taskId, v.assignmentId),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useAddComment() {
  const invalidate = useInvalidateSub("comments");
  return useMutation({
    mutationFn: (v: { taskId: string; body: string; mentions?: string[] }) =>
      tm.addComment(v.taskId, { body: v.body, mentions: v.mentions }),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useAddBlocker() {
  const invalidate = useInvalidateSub("blockers");
  return useMutation({
    mutationFn: (v: { taskId: string; blockerTaskId: string }) =>
      tm.addBlocker(v.taskId, v.blockerTaskId),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useRemoveBlocker() {
  const invalidate = useInvalidateSub("blockers");
  return useMutation({
    mutationFn: (v: { taskId: string; blockerId: string }) =>
      tm.removeBlocker(v.taskId, v.blockerId),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useAddAcl() {
  const invalidate = useInvalidateSub("acl");
  return useMutation({
    mutationFn: (v: {
      taskId: string;
      body: Parameters<typeof tm.addAcl>[1];
    }) => tm.addAcl(v.taskId, v.body),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useRemoveAcl() {
  const invalidate = useInvalidateSub("acl");
  return useMutation({
    mutationFn: (v: { taskId: string; aclId: string }) =>
      tm.removeAcl(v.taskId, v.aclId),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

export function useAddAttachment() {
  const invalidate = useInvalidateSub("attachments");
  return useMutation({
    mutationFn: (v: {
      taskId: string;
      body: Parameters<typeof tm.addAttachment>[1];
    }) => tm.addAttachment(v.taskId, v.body),
    onSuccess: (_d, v) => invalidate(v.taskId),
  });
}

// ── Listes ───────────────────────────────────────────────────────────────────

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof tm.createList>[0]) =>
      tm.createList(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K.lists }),
  });
}

export function useUpdateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      body: Parameters<typeof tm.updateList>[1];
    }) => tm.updateList(v.id, v.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K.lists }),
  });
}

export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tm.deleteList(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.lists });
      void qc.invalidateQueries({ queryKey: K.tasks });
    },
  });
}
